import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { RadioGroup } from '@headlessui/react';
import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/outline';
import { ButtonSpinner } from './ui/Spinner';

// Reusable Input Component
const Input = ({ label, type = "text", value, onChange, required = false, className = "", ...props }) => (
    <div className={`relative ${className}`}>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        <input
            type={type}
            value={value}
            onChange={onChange}
            required={required}
            className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 
                     bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white 
                     focus:border-primary-500 dark:focus:border-primary-400 
                     focus:outline-none focus:ring-1 focus:ring-primary-500 dark:focus:ring-primary-400
                     disabled:opacity-50 disabled:cursor-not-allowed"
            {...props}
        />
    </div>
);

const transactionTypes = [
    {
        id: 'deposit',
        name: 'Deposit',
        description: 'Add money to your bankroll',
        icon: ArrowUpIcon,
        color: 'text-green-600 dark:text-green-400',
        bgColor: 'bg-green-50 dark:bg-green-900/20',
        borderColor: 'border-green-200 dark:border-green-800'
    },
    {
        id: 'withdrawal',
        name: 'Withdrawal',
        description: 'Remove money from your bankroll',
        icon: ArrowDownIcon,
        color: 'text-red-600 dark:text-red-400',
        bgColor: 'bg-red-50 dark:bg-red-900/20',
        borderColor: 'border-red-200 dark:border-red-800'
    }
];

const BankrollTransactionForm = ({ onSuccess }) => {
    const [transactionType, setTransactionType] = useState('deposit');
    const [amount, setAmount] = useState('');
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [reason, setReason] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [storageLocations, setStorageLocations] = useState([]);
    const [selectedStorage, setSelectedStorage] = useState('');

    // Fetch storage locations on mount
    useEffect(() => {
        const fetchStorageLocations = async () => {
            try {
                const response = await axios.get('/api/bankroll-storage/');
                setStorageLocations(response.data);
            } catch (err) {
                console.error('Failed to fetch storage locations:', err);
            }
        };
        fetchStorageLocations();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        
        try {
            // Convert date to datetime string (using local timezone)
            const dateTime = new Date(date);
            dateTime.setHours(12, 0, 0, 0); // Set to noon to avoid timezone issues
            
            const payload = {
                amount: parseFloat(amount),
                date: dateTime.toISOString(),
                reason,
            };

            // Include storage_id if a storage location is selected
            if (selectedStorage) {
                payload.storage_id = parseInt(selectedStorage, 10);
            }

            if (transactionType === 'deposit') {
                await axios.post('/api/deposits/', payload);
            } else {
                await axios.post('/api/withdrawals/', payload);
            }
            
            setAmount('');
            setDate(new Date().toISOString().split('T')[0]);
            setReason('');
            setSelectedStorage('');
            if (onSuccess) {
                onSuccess();
            }
        } catch (err) {
            const errorMsg = err.response?.data?.error || `Failed to add ${transactionType}.`;
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const selectedType = transactionTypes.find(type => type.id === transactionType);

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            {/* Transaction Type Selection */}
            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Transaction Type
                </label>
                <RadioGroup value={transactionType} onChange={setTransactionType}>
                    <div className="grid grid-cols-2 gap-3">
                        {transactionTypes.map((type) => (
                            <RadioGroup.Option
                                key={type.id}
                                value={type.id}
                                className={({ checked }) =>
                                    `relative flex cursor-pointer rounded-lg border-2 p-4 transition-all ${
                                        checked
                                            ? `${type.bgColor} ${type.borderColor}`
                                            : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700'
                                    }`
                                }
                            >
                                {({ checked }) => (
                                    <>
                                        <div className="flex w-full items-center justify-between">
                                            <div className="flex items-center">
                                                <div className="text-sm">
                                                    <RadioGroup.Label
                                                        as="p"
                                                        className={`font-medium ${
                                                            checked ? type.color : 'text-gray-900 dark:text-white'
                                                        }`}
                                                    >
                                                        {type.name}
                                                    </RadioGroup.Label>
                                                    <RadioGroup.Description
                                                        as="span"
                                                        className={`inline-flex items-center text-xs ${
                                                            checked ? type.color : 'text-gray-500 dark:text-gray-400'
                                                        }`}
                                                    >
                                                        <type.icon className="h-4 w-4 mr-1" />
                                                        {type.description}
                                                    </RadioGroup.Description>
                                                </div>
                                            </div>
                                            <div className={`shrink-0 ${checked ? type.color : 'text-gray-400'}`}>
                                                <div className="h-6 w-6 rounded-full border-2 border-current flex items-center justify-center">
                                                    {checked && (
                                                        <div className="h-2 w-2 rounded-full bg-current" />
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </RadioGroup.Option>
                        ))}
                    </div>
                </RadioGroup>
            </div>

            <Input
                label="Amount"
                type="number"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                required
                min={0}
                step={0.01}
                placeholder={`Enter ${transactionType} amount`}
            />

            <Input
                label="Date"
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                required
            />

            <Input
                label={transactionType === 'deposit' ? 'Reason (Optional)' : 'Notes (Optional)'}
                type="text"
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder={transactionType === 'deposit' ? 'e.g., Monthly deposit, Bonus, etc.' : 'Optional notes...'}
            />

            {/* Storage Location Dropdown */}
            {storageLocations.length > 0 && (
                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {transactionType === 'deposit' ? 'To Storage (Optional)' : 'From Storage (Optional)'}
                    </label>
                    <select
                        value={selectedStorage}
                        onChange={e => setSelectedStorage(e.target.value)}
                        className="block w-full rounded-lg border border-gray-300 dark:border-gray-600
                                 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white
                                 focus:border-primary-500 dark:focus:border-primary-400
                                 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:focus:ring-primary-400"
                    >
                        <option value="">-- None --</option>
                        {storageLocations.map(storage => (
                            <option key={storage.id} value={storage.id}>
                                {storage.name} (${parseFloat(storage.amount).toFixed(2)})
                            </option>
                        ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {transactionType === 'deposit'
                            ? 'Add this amount to the selected storage location'
                            : 'Deduct this amount from the selected storage location'
                        }
                    </p>
                </div>
            )}

            <button
                type="submit"
                disabled={loading}
                className={`w-full px-4 py-2 text-white rounded-lg
                         hover:opacity-90 focus:outline-none focus:ring-2
                         focus:ring-offset-2 font-medium transition-all
                         disabled:opacity-50 disabled:cursor-not-allowed
                         ${transactionType === 'deposit' 
                             ? 'bg-green-600 focus:ring-green-500' 
                             : 'bg-red-600 focus:ring-red-500'
                         }`}
            >
                {loading ? (
                    <div className="flex items-center justify-center">
                        <ButtonSpinner />
                        <span className="ml-2">Processing...</span>
                    </div>
                ) : (
                    `Add ${selectedType.name}`
                )}
            </button>

            {error && (
                <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/30 
                              text-red-700 dark:text-red-400 border border-red-200 
                              dark:border-red-800 text-sm">
                    {error}
                </div>
            )}
        </form>
    );
};

export default BankrollTransactionForm; 